import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { formatINR, typeLabel } from "../lib/constants";
import Seo from "../components/Seo";

const Localities = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/localities").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <Seo
        title="Jaipur Localities Guide — Areas, Prices & What to Expect | Satyam Real Estate"
        description="Explore Jaipur's residential and commercial localities. Compare prices, property types and what buyers can expect in Mansarovar, Vaishali Nagar, C-Scheme, Malviya Nagar and more."
      />
      <nav className="text-sm text-stone-500 mb-4">
        <Link to="/" className="hover:text-brand">Home</Link> <span className="mx-1.5">/</span>
        <span className="text-stone-800">Localities</span>
      </nav>
      <h1 className="font-heading text-4xl sm:text-5xl text-stone-900">Explore Jaipur by locality</h1>
      <p className="text-stone-500 mt-3 max-w-2xl">A quick guide to what each area offers — property types, price ranges and the kind of lifestyle you can expect.</p>

      {loading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-10">
          {[...Array(6)].map((_, i) => <div key={i} className="h-72 rounded-2xl bg-stone-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mt-10" data-testid="localities-grid">
          {items.map((l) => (
            <Link key={l.slug} to={`/localities/${l.slug}`} data-testid={`locality-${l.slug}`}
              className="group bg-white rounded-2xl overflow-hidden border border-stone-200 hover-lift">
              <div className="relative aspect-[16/10] overflow-hidden bg-stone-100">
                {l.image && <img src={imageUrl(l.image)} alt={l.name} className="w-full h-full object-cover img-zoom" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="flex items-center gap-1.5 text-white/80 text-xs"><MapPin size={13} /> {l.city}</p>
                  <h3 className="font-heading text-2xl text-white">{l.name}</h3>
                </div>
              </div>
              <div className="p-5">
                {l.headline && <p className="text-sm text-stone-600 line-clamp-2 mb-3">{l.headline}</p>}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-stone-500">{l.count} listing{l.count > 1 ? "s" : ""}</span>
                  <span className="font-semibold text-stone-800">{formatINR(l.min_price)}+</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {l.types.slice(0, 3).map((t) => (
                    <span key={t} className="rounded-full bg-accent text-brand text-[11px] font-medium px-2.5 py-1">{typeLabel(t)}</span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand mt-4 group-hover:gap-2.5 transition-all">
                  View area guide <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Localities;
